import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as subscriptionActions } from 'modules/subscription/actions'

// Components
import { Section, Spinner } from '@tracklib/kit'
import CenteredText from 'components/staticpages/blocks/frontpage/centeredtext'
import Offer from 'components/subscribe/offer'
import PeriodSwitch from 'components/subscribe/periodswitch'

// Hooks
import { useStaticPageByPath } from 'modules/staticpages/hooks'
import { useIsAuthenticated } from 'modules/auth/hooks'
import { useSignupCode } from 'modules/signup/hooks'
import { useRequest } from 'modules/requests/hooks'

// Types
import { TierOffer, State } from 'types'

// Utils
import { isBrowser } from '@process'

import styles from './signupwithplans.css'

type SignupWithPlansProps = {
  value: { label?: string; title?: string; description?: string; link_text?: string; link_url?: string }
}

const SignupWithPlans = ({ value: { label, title, description, link_text, link_url } }: SignupWithPlansProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const [selectedMonths, setSelectedMonths] = useState<number | undefined>(undefined)
  const isAuthenticated = useIsAuthenticated()
  const { reftitle, reftext, refcode } = useSignupCode()
  const fetchTierOffersRequest = useRequest('FETCH_TIER_OFFERS', refcode)
  const offers = useSelector<State, Array<TierOffer>>(state => Object.values(state.subscription[refcode] || {}))
  const periods = (offers?.length && offers[0].tier.periods) || [] // if no chosen offer just select the first
  const selectedPeriod = periods.find(({ months }) => months === selectedMonths)
  const { page } = useStaticPageByPath('subscribe')
  const perks = page?.content.find(x => x.type === 'offer')?.value.perks

  useEffect(() => {
    if (!fetchTierOffersRequest.isLoading && !fetchTierOffersRequest.isDone) {
      dispatch(subscriptionActions.fetchTierOffers(refcode))
    }
  }, [dispatch, refcode, fetchTierOffersRequest])

  useEffect(() => {
    // NOTE(Samuel): Set default month (not id as that would proceed to pay directly)
    if (offers.length > 0 && !selectedMonths) {
      const defaultOffer = offers.find(({ is_default }) => is_default) || offers[0]
      const defaultPeriod = defaultOffer.tier.periods.sort((a, b) => a.months - b.months)[0]
      setSelectedMonths(defaultPeriod.months)
    }
  }, [offers, selectedMonths, dispatch])

  if (isAuthenticated) {
    return null
  }

  if (!offers?.length || !isBrowser) {
    return <Spinner minHeight={200} delay={0} />
  }

  const textTitle = reftitle || title
  const textDescription = reftext || description

  return (
    <Section extraPad className={styles.sectionWrapper}>
      <CenteredText
        link_url={link_url}
        link_text={link_text}
        description={textDescription}
        title={textTitle}
        label={label}
        className={styles.text}
      />
      <PeriodSwitch periods={periods} selectedMonths={selectedMonths as number} setSelectedMonths={setSelectedMonths} />
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {offers
            .sort((a, b) => a.tier.ordering - b.tier.ordering)
            .map(({ id, tier }) => {
              const selectedPeriodForOffer =
                tier.periods.find(x => x.months === selectedPeriod?.months) || tier.periods.sort((a, b) => b.months - a.months)[0]
              return <Offer key={id} tier={tier} period={selectedPeriodForOffer} perks={perks} />
            })}
        </div>
      </div>
    </Section>
  )
}

export default SignupWithPlans
